import { useMutation, useQuery, useQueryClient, type UseMutationResult, type UseQueryResult } from '@tanstack/react-query'
import type { SalesBusinessDto, SalesBusinessesDto, VenueSalesBusinessDto } from '@lib/dto/sales-business.dto'
import type { BookingDto, SigningLinkDto } from '@lib/dto/sales-booking.dto'
import type { PaymentIntentDto, PublicPaymentDto } from '@lib/dto/sales-payment.dto'
import type { DepositReturnInput, OutsidePaymentInput, PaymentIntentInput, RefundInput } from '@lib/input/sales-payment.input'
import type { EventSalesDto, SalesPacketDto, SentPacketDto } from '@lib/dto/sales-packet.dto'
import type { SalesPriceListDto } from '@lib/dto/sales-price-list.dto'
import type { SalesReadinessDto, SalesSchedulesDto, SalesSettingsDto, SalesTemplatesDto } from '@lib/dto/sales-setup.dto'
import type { CreateAmendmentInput, CreateInvoiceInput, CreatePacketInput, ExtendPacketInput, PacketChoiceInput, SetCountersignerInput } from '@lib/input/sales-packet.input'
import type { PutSalesPriceListInput } from '@lib/input/sales-price-list.input'
import type { PutSalesSchedulesInput, PutSalesSettingsInput, PutSalesTemplatesInput } from '@lib/input/sales-setup.input'
import { salesService } from '@/services/SalesService'
import { eventKeys, salesKeys } from './queryKeys'

/** A venue's price list. */
export function useSalesPriceList(venueId: string): UseQueryResult<SalesPriceListDto> {
  return useQuery({
    queryKey: salesKeys.priceList(venueId),
    queryFn: () => salesService.priceList(venueId),
    enabled: venueId !== '',
    retry: false,
  })
}

/** Saves a venue's price list whole. */
export function useSaveSalesPriceList(venueId: string): UseMutationResult<SalesPriceListDto, Error, PutSalesPriceListInput> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: PutSalesPriceListInput) => salesService.savePriceList(venueId, input),
    onSuccess: (saved) => {
      queryClient.setQueryData(salesKeys.priceList(venueId), saved)
      void queryClient.invalidateQueries({ queryKey: salesKeys.readiness(venueId) })
    },
  })
}

export function useSalesTemplates(venueId: string): UseQueryResult<SalesTemplatesDto> {
  return useQuery({ queryKey: salesKeys.templates(venueId), queryFn: () => salesService.templates(venueId), enabled: venueId !== '', retry: false })
}

export function useSaveSalesTemplates(venueId: string): UseMutationResult<SalesTemplatesDto, Error, PutSalesTemplatesInput> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: PutSalesTemplatesInput) => salesService.saveTemplates(venueId, input),
    onSuccess: (saved) => {
      queryClient.setQueryData(salesKeys.templates(venueId), saved)
      void queryClient.invalidateQueries({ queryKey: salesKeys.readiness(venueId) })
    },
  })
}

export function useSalesSchedules(venueId: string): UseQueryResult<SalesSchedulesDto> {
  return useQuery({ queryKey: salesKeys.schedules(venueId), queryFn: () => salesService.schedules(venueId), enabled: venueId !== '', retry: false })
}

export function useSaveSalesSchedules(venueId: string): UseMutationResult<SalesSchedulesDto, Error, PutSalesSchedulesInput> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: PutSalesSchedulesInput) => salesService.saveSchedules(venueId, input),
    onSuccess: (saved) => {
      queryClient.setQueryData(salesKeys.schedules(venueId), saved)
      void queryClient.invalidateQueries({ queryKey: salesKeys.readiness(venueId) })
    },
  })
}

export function useSalesSettings(venueId: string): UseQueryResult<SalesSettingsDto> {
  return useQuery({ queryKey: salesKeys.settings(venueId), queryFn: () => salesService.settings(venueId), enabled: venueId !== '', retry: false })
}

export function useSaveSalesSettings(venueId: string): UseMutationResult<SalesSettingsDto, Error, PutSalesSettingsInput> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: PutSalesSettingsInput) => salesService.saveSettings(venueId, input),
    onSuccess: (saved) => queryClient.setQueryData(salesKeys.settings(venueId), saved),
  })
}

/** What a venue still needs before it can send a packet. */
export function useSalesReadiness(venueId: string): UseQueryResult<SalesReadinessDto> {
  return useQuery({ queryKey: salesKeys.readiness(venueId), queryFn: () => salesService.readiness(venueId), enabled: venueId !== '', retry: false })
}

/** The company's business and each venue's own. Company-wide integrations only. */
export function useSalesBusinesses(): UseQueryResult<SalesBusinessesDto> {
  return useQuery({ queryKey: salesKeys.businesses(), queryFn: () => salesService.businesses(), retry: false })
}

/** Who a venue's payments go to. */
export function useVenueSalesBusiness(venueId: string): UseQueryResult<VenueSalesBusinessDto> {
  return useQuery({ queryKey: salesKeys.venueBusiness(venueId), queryFn: () => salesService.venueBusiness(venueId), enabled: venueId !== '', retry: false })
}

/** Starts the company's business (null) or a venue's own; everything about who gets paid is read again. */
export function useCreateSalesBusiness(): UseMutationResult<SalesBusinessDto, Error, string | null> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (venueId: string | null) => salesService.createBusiness(venueId),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: salesKeys.all }),
  })
}

/** An event's Sales section: its proposals, and who can sign and countersign one. */
export function useEventSales(eventId: string, enabled = true): UseQueryResult<EventSalesDto> {
  return useQuery({ queryKey: salesKeys.event(eventId), queryFn: () => salesService.eventSales(eventId), enabled: enabled && eventId !== '', retry: false })
}

/**
 * What changes when a proposal is sent or closed: the event's Sales section,
 * and the lanes every other booking form asks about, which it now holds or
 * lets go.
 */
function useProposalChanged(eventId: string): () => Promise<void> {
  const queryClient = useQueryClient()
  return async () => {
    await queryClient.invalidateQueries({ queryKey: salesKeys.event(eventId) })
    await queryClient.invalidateQueries({ queryKey: eventKeys.all })
  }
}

/** Sends an inquiry a proposal. */
export function useSendPacket(eventId: string): UseMutationResult<SentPacketDto, Error, CreatePacketInput> {
  const changed = useProposalChanged(eventId)
  return useMutation({ mutationFn: (input: CreatePacketInput) => salesService.sendPacket(eventId, input), onSuccess: changed })
}

/** Invoices a booked event for extras. */
export function useSendInvoice(eventId: string): UseMutationResult<SalesPacketDto, Error, CreateInvoiceInput> {
  const changed = useProposalChanged(eventId)
  return useMutation({ mutationFn: (input: CreateInvoiceInput) => salesService.sendInvoice(eventId, input), onSuccess: changed })
}

/** Sends a booked event a change to its agreement. */
export function useSendAmendment(eventId: string): UseMutationResult<SentPacketDto, Error, CreateAmendmentInput> {
  const changed = useProposalChanged(eventId)
  return useMutation({ mutationFn: (input: CreateAmendmentInput) => salesService.sendAmendment(eventId, input), onSuccess: changed })
}

/** Withdraws a proposal. */
export function useWithdrawPacket(eventId: string): UseMutationResult<SalesPacketDto, Error, string> {
  const changed = useProposalChanged(eventId)
  return useMutation({ mutationFn: (packetId: string) => salesService.withdrawPacket(eventId, packetId), onSuccess: changed })
}

/** Gives a proposal a new last day. */
export function useExtendPacket(eventId: string): UseMutationResult<SalesPacketDto, Error, { packetId: string } & ExtendPacketInput> {
  const changed = useProposalChanged(eventId)
  return useMutation({
    mutationFn: ({ packetId, ...input }: { packetId: string } & ExtendPacketInput) => salesService.extendPacket(eventId, packetId, input),
    onSuccess: changed,
  })
}

/** Changes who countersigns a proposal. */
export function useSetCountersigner(eventId: string): UseMutationResult<SalesPacketDto, Error, { packetId: string } & SetCountersignerInput> {
  const changed = useProposalChanged(eventId)
  return useMutation({
    mutationFn: ({ packetId, ...input }: { packetId: string } & SetCountersignerInput) => salesService.setCountersigner(eventId, packetId, input),
    onSuccess: changed,
  })
}

/** An event's proposals, as its couple and planner see them. */
export function useBooking(eventId: string, enabled = true): UseQueryResult<BookingDto> {
  return useQuery({ queryKey: salesKeys.booking(eventId), queryFn: () => salesService.booking(eventId), enabled: enabled && eventId !== '', retry: false })
}

/** Saves the package and add-ons the couple picked. */
export function useChoosePacket(eventId: string): UseMutationResult<BookingDto, Error, { packetId: string } & PacketChoiceInput> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ packetId, ...input }: { packetId: string } & PacketChoiceInput) => salesService.choosePacket(eventId, packetId, input),
    onSuccess: (booking) => queryClient.setQueryData(salesKeys.booking(eventId), booking),
  })
}

/** Opens the contract for one of the couple to sign. */
export function useOpenContract(eventId: string): UseMutationResult<SigningLinkDto, Error, string> {
  return useMutation({ mutationFn: (packetId: string) => salesService.openContract(eventId, packetId) })
}

/** Opens the contract for the venue's countersigner. */
export function useCountersign(eventId: string): UseMutationResult<SigningLinkDto, Error, string> {
  return useMutation({ mutationFn: (packetId: string) => salesService.countersign(eventId, packetId) })
}

/** Records a payment received outside the app. */
export function useRecordPayment(eventId: string): UseMutationResult<EventSalesDto, Error, { paymentId: string } & OutsidePaymentInput> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ paymentId, ...input }: { paymentId: string } & OutsidePaymentInput) => salesService.recordPayment(eventId, paymentId, input),
    onSuccess: async (sales) => {
      queryClient.setQueryData(salesKeys.event(eventId), sales)
      // A deposit can book the event, which changes its page and every list it's on.
      await queryClient.invalidateQueries({ queryKey: eventKeys.all })
    },
  })
}

/** Gives back all or part of a payment, with a reason. */
export function useRefund(eventId: string): UseMutationResult<EventSalesDto, Error, { paymentId: string; recordId: string } & RefundInput> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ paymentId, recordId, ...input }: { paymentId: string; recordId: string } & RefundInput) => salesService.refund(eventId, paymentId, recordId, input),
    onSuccess: (sales) => queryClient.setQueryData(salesKeys.event(eventId), sales),
  })
}

/** Settles a security deposit after the event: returns some, all or none of it. */
export function useReturnDeposit(eventId: string): UseMutationResult<EventSalesDto, Error, { paymentId: string } & DepositReturnInput> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ paymentId, ...input }: { paymentId: string } & DepositReturnInput) => salesService.returnDeposit(eventId, paymentId, input),
    onSuccess: (sales) => queryClient.setQueryData(salesKeys.event(eventId), sales),
  })
}

/**
 * A payment through its link. While money is on its way it checks again every
 * few seconds, until Stripe's word arrives and it reads paid.
 */
export function usePublicPayment(token: string, waiting: boolean): UseQueryResult<PublicPaymentDto> {
  return useQuery({
    queryKey: salesKeys.pay(token),
    queryFn: () => salesService.publicPayment(token),
    enabled: token !== '',
    retry: false,
    refetchInterval: (query) => (waiting && query.state.data?.status !== 'paid' ? 3_000 : false),
  })
}

/** Starts paying through a link; saving the method for autopay goes through the couple's own, signed-in route. */
export function useStartPayment(token: string): UseMutationResult<PaymentIntentDto, Error, PaymentIntentInput> {
  return useMutation({
    mutationFn: (input: PaymentIntentInput) => (input.saveForLater === true ? salesService.startAutopayPayment(token, input) : salesService.startPayment(token, input)),
  })
}

/** Switches autopay off. */
export function useTurnOffAutopay(eventId: string): UseMutationResult<void, Error, void> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () => salesService.turnOffAutopay(eventId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: salesKeys.booking(eventId) }),
  })
}
